import React, { useCallback, useMemo } from 'react';
import LanguageKeysTH, { type LangProps } from './th';
import LanguageKeys from './en';
import { setCookie, getCookie } from 'helper/cookie';

const th = new LanguageKeysTH();
const en = new LanguageKeys();

interface State { lang: string; keys: LanguageKeysTH; }

const LangStateContext = React.createContext<State | undefined>(undefined);
const LangUpdaterContext = React.createContext<
  React.Dispatch<React.SetStateAction<'en' | 'th'>> | undefined
>(undefined);

const LangProvider: React.FunctionComponent<any> = ({ children }) => {
  const cookieLang: any = getCookie('lang') ? getCookie('lang') : 'th';
  const [lang, setLang] = React.useState<'en' | 'th'>(cookieLang || 'th');
  setCookie('lang', lang);
  const value: any = useMemo(() => {
    return {
      lang,
      keys: lang === 'th' ? th : en
    };
  }, [lang]);

  return (
    <LangStateContext.Provider value={value}>
      <LangUpdaterContext.Provider value={setLang}>
        {children}
      </LangUpdaterContext.Provider>
    </LangStateContext.Provider>
  );
};

function useLangState() {
  const langState = React.useContext(LangStateContext);
  if (langState === undefined) {
    throw new Error('useLangState must be used within a LangProvider');
  }

  return langState;
}

function useT() {
  const langState = useLangState();

  const t = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (id: LangProps, data?: object): string => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const textItem: LanguageKeysTH = (langState.keys as any)[id];
      const str: LanguageKeysTH = textItem || id;

      return textItem instanceof Function ? textItem(data) : str;
    },
    [langState]
  );

  return t;
}

function useLang() {
  const langState = useLangState();

  return langState.lang;
}

function useSetLang() {
  const setLang = React.useContext(LangUpdaterContext);
  if (setLang === undefined) {
    throw new Error('useSetLang must be used within a LangProvider');
  }

  return setLang;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface Props { id: LangProps; data?: object; }

// eslint-disable-next-line react/display-name
const T = React.memo(({ id, data }: Props) => {
  const t = useT();

  return <>{t(id, data)}</>;
});

export { LangProvider, useSetLang, useT, useLang, T };
